import { startTransition, useEffect, useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { env } from "@/lib/env"
import { apiClient, getApiFailure, type FleetPilotLeadRecord } from "@/lib/api/client"

type FleetSegment = "trucking" | "intercity_bus" | "delivery_rider"

interface FleetPilotFormState {
  city: string
  companyName: string
  contactName: string
  currentSafetyStack: string
  estimatedVehicleCount: string
  fleetSegment: FleetSegment
  fleetSizeBand: string
  operatingRegion: string
  phone: string
  pilotGoals: string
  preferredDemoWindow: string
  rolloutTimeline: string
  workEmail: string
}

interface BannerState {
  message: string
  tone: "error" | "info" | "success"
}

const CONCEPT_NOTE_DOWNLOAD_URL = "/fleet-pilot-concept-note.md"
const FLEET_PILOT_PAGE_TITLE = "Fleet Pilot Intake | Traffic by FeedbackNFC"
const FLEET_PILOT_PAGE_DESCRIPTION =
  "Submit a Fleet Safety pilot intake for trucking, intercity bus, and delivery operations with SafetyQR incident-response workflows."

const SEGMENT_OPTIONS: Array<{
  description: string
  label: string
  value: FleetSegment
}> = [
  {
    value: "trucking",
    label: "Trucking and logistics",
    description:
      "Long-haul trucks, cargo carriers, and logistics fleets that need incident-response discipline across routes and depots.",
  },
  {
    value: "intercity_bus",
    label: "Intercity and staff buses",
    description:
      "Passenger and staff transport operators managing safety escalations, complaint handling, and route-wide governance.",
  },
  {
    value: "delivery_rider",
    label: "Delivery rider fleets",
    description:
      "High-volume rider operations looking to standardize panic escalation and evidence quality during live dispatch.",
  },
]

const FLEET_SIZE_BANDS = [
  "1-50 vehicles",
  "51-200 vehicles",
  "201-1000 vehicles",
  "1000+ vehicles",
]

const ROLLOUT_TIMELINE_OPTIONS = [
  "Immediately (0-30 days)",
  "This quarter",
  "Next quarter",
  "Exploration only",
]

const INPUT_CLASS =
  "mt-2 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-32 resize-y`
const LABEL_CLASS = "text-sm font-medium text-foreground"

const INITIAL_FORM_STATE: FleetPilotFormState = {
  city: "",
  companyName: "",
  contactName: "",
  currentSafetyStack: "",
  estimatedVehicleCount: "",
  fleetSegment: "trucking",
  fleetSizeBand: "",
  operatingRegion: "",
  phone: "",
  pilotGoals: "",
  preferredDemoWindow: "",
  rolloutTimeline: "",
  workEmail: "",
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function segmentLabel(value: string | null | undefined) {
  return (
    SEGMENT_OPTIONS.find((segment) => segment.value === value)?.label
    || "Fleet segment"
  )
}

export function PublicFleetPilotPage() {
  const [form, setForm] = useState<FleetPilotFormState>(INITIAL_FORM_STATE)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [banner, setBanner] = useState<BannerState | null>(null)
  const [submittedLead, setSubmittedLead] = useState<FleetPilotLeadRecord | null>(null)

  const hasSchedulingUrl = Boolean(env.fleetPilotSchedulingUrl)
  const selectedSegment = useMemo(
    () =>
      SEGMENT_OPTIONS.find((segment) => segment.value === form.fleetSegment)
      || SEGMENT_OPTIONS[0],
    [form.fleetSegment]
  )

  useEffect(() => {
    const previousTitle = document.title
    const previousDescription = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content")
    const previousOgTitle = document
      .querySelector('meta[property="og:title"]')
      ?.getAttribute("content")
    const previousOgDescription = document
      .querySelector('meta[property="og:description"]')
      ?.getAttribute("content")
    const previousTwitterTitle = document
      .querySelector('meta[name="twitter:title"]')
      ?.getAttribute("content")
    const previousTwitterDescription = document
      .querySelector('meta[name="twitter:description"]')
      ?.getAttribute("content")

    document.title = FLEET_PILOT_PAGE_TITLE
    setMetaContent('meta[name="description"]', FLEET_PILOT_PAGE_DESCRIPTION)
    setMetaContent('meta[property="og:title"]', FLEET_PILOT_PAGE_TITLE)
    setMetaContent('meta[property="og:description"]', FLEET_PILOT_PAGE_DESCRIPTION)
    setMetaContent('meta[name="twitter:title"]', FLEET_PILOT_PAGE_TITLE)
    setMetaContent('meta[name="twitter:description"]', FLEET_PILOT_PAGE_DESCRIPTION)

    return () => {
      document.title = previousTitle
      restoreMetaContent('meta[name="description"]', previousDescription)
      restoreMetaContent('meta[property="og:title"]', previousOgTitle)
      restoreMetaContent('meta[property="og:description"]', previousOgDescription)
      restoreMetaContent('meta[name="twitter:title"]', previousTwitterTitle)
      restoreMetaContent('meta[name="twitter:description"]', previousTwitterDescription)
    }
  }, [])

  function updateForm<K extends keyof FleetPilotFormState>(
    key: K,
    value: FleetPilotFormState[K]
  ) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }))
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBanner(null)

    if (!form.companyName.trim() || !form.contactName.trim()) {
      setBanner({
        message: "Company name and primary contact are required to qualify the pilot.",
        tone: "error",
      })
      return
    }

    if (!form.workEmail.trim() || !form.phone.trim()) {
      setBanner({
        message: "Work email and phone are required for operator follow-up.",
        tone: "error",
      })
      return
    }

    if (!form.pilotGoals.trim()) {
      setBanner({
        message: "Describe your pilot goals so our team can scope the rollout properly.",
        tone: "error",
      })
      return
    }

    const estimatedVehicleCount = form.estimatedVehicleCount.trim()
      ? Number.parseInt(form.estimatedVehicleCount.trim(), 10)
      : null

    if (
      estimatedVehicleCount !== null
      && (!Number.isFinite(estimatedVehicleCount) || estimatedVehicleCount < 1)
    ) {
      setBanner({
        message: "Estimated active vehicles must be a positive number.",
        tone: "error",
      })
      return
    }

    setIsSubmitting(true)

    try {
      const response = await apiClient.submitFleetPilotLead({
        city: form.city.trim() || null,
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        currentSafetyStack: form.currentSafetyStack.trim() || null,
        estimatedVehicleCount,
        fleetSegment: form.fleetSegment,
        fleetSizeBand: form.fleetSizeBand.trim() || null,
        operatingRegion: form.operatingRegion.trim() || null,
        phone: form.phone.trim(),
        pilotGoals: form.pilotGoals.trim(),
        preferredDemoWindow: form.preferredDemoWindow.trim() || null,
        rolloutTimeline: form.rolloutTimeline.trim() || null,
        sourceSurface: "fleet_safety_pilot_form",
        workEmail: form.workEmail.trim(),
      })

      startTransition(() => {
        setSubmittedLead(response.data)
        setBanner({
          message:
            "Your fleet pilot lead is now recorded. Our team will review it and follow up with next steps.",
          tone: "success",
        })
      })
    } catch (error) {
      const failure = getApiFailure(error)
      setBanner({
        message:
          failure?.error
          || (error instanceof Error
            ? error.message
            : "Pilot submission failed. Please retry."),
        tone: "error",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="grid gap-5">
      <Card className="shell-panel overflow-hidden rounded-4xl border-border/70">
        <CardHeader className="gap-4 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.2),transparent_35%),linear-gradient(135deg,rgba(15,23,42,0.07),rgba(15,23,42,0.01))]">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary">Fleet pilot intake</Badge>
            <Badge variant="outline">/fleet-safety/pilot</Badge>
          </div>
          <CardTitle className="font-heading text-3xl leading-tight sm:text-4xl">
            Book a Fleet Safety pilot
          </CardTitle>
          <CardDescription className="max-w-3xl text-base leading-7 text-muted-foreground">
            This intake is built for fleet operators evaluating SafetyQR for trucking,
            intercity bus, and delivery rider workflows. Every submission is persisted
            for operator review and pilot planning.
          </CardDescription>
          <div className="grid max-w-3xl gap-3">
            <div className="flex flex-wrap gap-3">
              <Button asChild variant="outline">
                <a download href={CONCEPT_NOTE_DOWNLOAD_URL}>
                  Download pilot concept note
                </a>
              </Button>
              {hasSchedulingUrl ? (
                <Button asChild>
                  <a
                    href={env.fleetPilotSchedulingUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Schedule a demo call
                  </a>
                </Button>
              ) : null}
            </div>
            {hasSchedulingUrl ? null : (
              <div className="rounded-2xl border border-dashed border-border/80 bg-background/70 px-4 py-3 text-sm text-muted-foreground">
                Demo scheduling opens here once your scheduling URL is configured.
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {banner ? <Banner banner={banner} /> : null}

      {submittedLead ? (
        <Card className="shell-panel rounded-4xl border-border/70">
          <CardHeader className="gap-3">
            <Badge variant="secondary">Lead captured</Badge>
            <CardTitle className="font-heading text-2xl">
              {submittedLead.companyName || "Fleet lead"} is queued for pilot review
            </CardTitle>
            <CardDescription className="text-base leading-7 text-muted-foreground">
              Reference <strong>{submittedLead.id}</strong> if you need to follow up with
              the operations team.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <FactCard label="Status" value={submittedLead.status || "new"} />
            <FactCard
              label="Submitted"
              value={formatDateTime(submittedLead.createdAt)}
            />
            <FactCard
              label="Fleet segment"
              value={segmentLabel(submittedLead.fleetSegment)}
            />
            <FactCard
              label="Notification"
              value={submittedLead.notificationStatus || "pending"}
            />
          </CardContent>
        </Card>
      ) : null}

      <Card className="shell-panel rounded-4xl border-border/70">
        <CardHeader className="px-4 pt-0 sm:px-6">
          <CardTitle className="font-heading text-2xl">
            Fleet profile and qualification
          </CardTitle>
          <CardDescription className="text-base leading-7 text-muted-foreground">
            {selectedSegment.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 px-4 pb-0 sm:px-6">
          <form className="grid gap-5" onSubmit={handleSubmit}>
            <div className="grid gap-3 md:grid-cols-3">
              {SEGMENT_OPTIONS.map((option) => {
                const isSelected = option.value === form.fleetSegment
                return (
                  <button
                    key={option.value}
                    className={
                      isSelected
                        ? "rounded-3xl border border-primary/40 bg-primary/5 px-4 py-4 text-left shadow-sm transition"
                        : "rounded-3xl border border-border/70 bg-background/70 px-4 py-4 text-left transition hover:border-primary/30 hover:bg-background"
                    }
                    type="button"
                    onClick={() => updateForm("fleetSegment", option.value)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <strong className="text-sm">{option.label}</strong>
                      {isSelected ? <Badge variant="secondary">Selected</Badge> : null}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {option.description}
                    </p>
                  </button>
                )
              })}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className={LABEL_CLASS}>Company name</span>
                <input
                  aria-label="Company name"
                  className={INPUT_CLASS}
                  placeholder="Acme Logistics Private Ltd"
                  value={form.companyName}
                  onChange={(event) => updateForm("companyName", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Primary contact</span>
                <input
                  aria-label="Primary contact"
                  className={INPUT_CLASS}
                  placeholder="Operations head or safety owner"
                  value={form.contactName}
                  onChange={(event) => updateForm("contactName", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Work email</span>
                <input
                  aria-label="Work email"
                  className={INPUT_CLASS}
                  placeholder="ops@fleetco.com"
                  type="email"
                  value={form.workEmail}
                  onChange={(event) => updateForm("workEmail", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Phone</span>
                <input
                  aria-label="Phone"
                  className={INPUT_CLASS}
                  placeholder="+1 555 123 4567"
                  value={form.phone}
                  onChange={(event) => updateForm("phone", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Primary city</span>
                <input
                  aria-label="Primary city"
                  className={INPUT_CLASS}
                  placeholder="Bengaluru"
                  value={form.city}
                  onChange={(event) => updateForm("city", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Operating region</span>
                <input
                  aria-label="Operating region"
                  className={INPUT_CLASS}
                  placeholder="South India corridor"
                  value={form.operatingRegion}
                  onChange={(event) => updateForm("operatingRegion", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Fleet size band</span>
                <select
                  aria-label="Fleet size band"
                  className={INPUT_CLASS}
                  value={form.fleetSizeBand}
                  onChange={(event) => updateForm("fleetSizeBand", event.target.value)}
                >
                  <option value="">Select fleet size</option>
                  {FLEET_SIZE_BANDS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Estimated active vehicles</span>
                <input
                  aria-label="Estimated active vehicles"
                  className={INPUT_CLASS}
                  inputMode="numeric"
                  placeholder="250"
                  value={form.estimatedVehicleCount}
                  onChange={(event) =>
                    updateForm("estimatedVehicleCount", event.target.value)
                  }
                />
              </label>

              <label className="block md:col-span-2">
                <span className={LABEL_CLASS}>Current safety stack and workflow</span>
                <textarea
                  aria-label="Current safety stack and workflow"
                  className={TEXTAREA_CLASS}
                  placeholder="Tools used today (GPS, dispatch, escalation), existing incident process, and where breakdowns happen."
                  value={form.currentSafetyStack}
                  onChange={(event) =>
                    updateForm("currentSafetyStack", event.target.value)
                  }
                />
              </label>

              <label className="block md:col-span-2">
                <span className={LABEL_CLASS}>Pilot goals</span>
                <textarea
                  aria-label="Pilot goals"
                  className={TEXTAREA_CLASS}
                  placeholder="What should improve in the pilot? Example: panic response SLAs, complaint closure times, evidence quality, auditable escalation."
                  value={form.pilotGoals}
                  onChange={(event) => updateForm("pilotGoals", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Rollout timeline</span>
                <select
                  aria-label="Rollout timeline"
                  className={INPUT_CLASS}
                  value={form.rolloutTimeline}
                  onChange={(event) => updateForm("rolloutTimeline", event.target.value)}
                >
                  <option value="">Select timeline</option>
                  {ROLLOUT_TIMELINE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Preferred demo window</span>
                <input
                  aria-label="Preferred demo window"
                  className={INPUT_CLASS}
                  placeholder="Tue-Thu, 10:00-14:00 IST"
                  value={form.preferredDemoWindow}
                  onChange={(event) =>
                    updateForm("preferredDemoWindow", event.target.value)
                  }
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button disabled={isSubmitting} type="submit">
                {isSubmitting ? "Submitting..." : "Submit fleet pilot lead"}
              </Button>
              {submittedLead ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSubmittedLead(null)
                    setBanner(null)
                    setForm(INITIAL_FORM_STATE)
                  }}
                >
                  Submit another lead
                </Button>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  )
}

function setMetaContent(selector: string, content: string) {
  const metaElement = document.querySelector<HTMLMetaElement>(selector)
  if (metaElement) {
    metaElement.setAttribute("content", content)
  }
}

function restoreMetaContent(selector: string, content: string | null | undefined) {
  const metaElement = document.querySelector<HTMLMetaElement>(selector)
  if (!metaElement || content === undefined || content === null) {
    return
  }

  metaElement.setAttribute("content", content)
}

function FactCard(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {props.label}
      </div>
      <div className="mt-3 text-sm leading-6">{props.value}</div>
    </div>
  )
}

function Banner({ banner }: { banner: BannerState }) {
  return (
    <div
      className={
        banner.tone === "error"
          ? "rounded-3xl border border-destructive/30 bg-destructive/8 px-4 py-3 text-sm leading-6 text-destructive"
          : banner.tone === "success"
            ? "rounded-3xl border border-emerald-300/40 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900"
            : "rounded-3xl border border-border/70 bg-background/70 px-4 py-3 text-sm leading-6 text-foreground"
      }
    >
      {banner.message}
    </div>
  )
}
